import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
  BookTextIcon,
  HelpCircleIcon,
  HistoryIcon,
  PlusIcon,
  SendHorizontalIcon,
  SquarePenIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import {
  ExampleQuestions,
  INTENTS,
  MilaGreeting,
  PII_NOTE,
  type AskMode,
  type Intent,
} from './KennisagentComposer.js';

/**
 * Mobiele schil van de Mila-pagina (< md). Gekozen uit drie prototypes op
 * 10 sep 2026 (Jos): begroeting + voorbeeldvragen als lege staat, slanke
 * werkbalk boven (gesprekken + uitleg), invoerveld vast onderaan met verzendknop erin, en de vier
 * richtingen + zoekbereik + gesprekken achter een plus-knop in een
 * bottom sheet. Een gekozen richting blijft als chip in het veld staan tot
 * de vraag verstuurd is. Desktop (KennisagentComposer) volgt hetzelfde model.
 */

export interface KennisagentMobileProps {
  hasTurns: boolean;
  /** Beurten + wachtstaat + fouten + naamBevestiging, gerenderd door de pagina. */
  conversation: ReactNode;
  /** Sara-contextbalk (of null); staat boven het invoerveld. */
  composerBanner: ReactNode;
  draft: string;
  setDraft: (v: string) => void;
  inputRef: RefObject<HTMLTextAreaElement>;
  submit: (mode: AskMode) => void;
  pending: boolean;
  /** Sticky modus uit een Sara-sessie; wint van de vrije modus. */
  stickyMode: AskMode | null;
  scopeLabel: string;
  scopeActive: boolean;
  onOpenScope: () => void;
  onClearScope: () => void;
  onOpenHistory: () => void;
  onNewChat: () => void;
}

export function KennisagentMobile(p: KennisagentMobileProps): JSX.Element {
  const [armed, setArmed] = useState<Intent | null>(null);
  const [sheet, setSheet] = useState(false);
  const armedIntent = INTENTS.find((i) => i.mode === armed);
  const canSend = !p.pending && (p.draft.trim().length > 0 || armed !== null);

  const send = (): void => {
    if (!canSend) return;
    p.submit(armed ?? p.stickyMode ?? 'free');
    setArmed(null);
  };

  // Auto-grow tot ~6 regels; daarna scrollt het veld zelf.
  useEffect(() => {
    const el = p.inputRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [p.draft, p.inputRef]);

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-brand-50 bg-white px-2 py-1.5">
        <button
          type="button"
          onClick={p.onOpenHistory}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium text-ink-900/70 hover:bg-brand-50"
        >
          <HistoryIcon size={15} /> Gesprekken
        </button>
        <Link
          to="/help"
          aria-label="Uitleg over Mila"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium text-ink-900/70 hover:bg-brand-50"
        >
          <HelpCircleIcon size={15} /> Uitleg
        </Link>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {!p.hasTurns && (
          <>
            <div className="px-1 pt-6">
              <MilaGreeting compact />
            </div>
            <div className="mt-5">
              <ExampleQuestions
                onPick={(q) => {
                  p.setDraft(q);
                  p.inputRef.current?.focus();
                }}
              />
            </div>
          </>
        )}
        <div className="pt-4">{p.conversation}</div>
      </div>

      <div className="shrink-0 border-t border-brand-50 bg-white px-3 pb-[max(env(safe-area-inset-bottom),10px)] pt-2">
        {p.composerBanner && <div className="mb-2">{p.composerBanner}</div>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSheet(true)}
            aria-label="Meer opties"
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${
              p.scopeActive
                ? 'border-brand-700 bg-brand-50 text-brand-800'
                : 'border-ring2 text-ink-900/70'
            }`}
          >
            <PlusIcon size={20} />
          </button>
          <div className="min-w-0 flex-1 rounded-3xl border border-ring2 bg-white shadow-card focus-within:border-brand-700">
            {armedIntent && (
              <div className="flex items-center gap-1.5 px-3 pt-2 text-[12px] font-medium text-brand-800">
                <armedIntent.Icon size={13} /> {armedIntent.label}
                <button
                  type="button"
                  onClick={() => setArmed(null)}
                  aria-label="Richting loslaten"
                  className="ml-1 text-ink-900/40"
                >
                  <XIcon size={13} />
                </button>
              </div>
            )}
            <div className="flex items-end gap-1 py-1.5 pr-1.5">
              {/* 16px: onder die grens zoomt iOS Safari in bij focus (Jos, 10 sep 2026). */}
              <textarea
                ref={p.inputRef}
                value={p.draft}
                onChange={(e) => p.setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                disabled={p.pending}
                maxLength={4000}
                rows={1}
                placeholder={
                  armed
                    ? 'Beschrijf de situatie of het onderwerp…'
                    : p.hasTurns
                      ? 'Vervolgvraag…'
                      : 'Stel je vraag aan Mila…'
                }
                className="max-h-40 min-h-[36px] w-full resize-none bg-transparent px-3 py-2 text-[16px] leading-snug text-ink-900 placeholder:text-ink-900/35 focus:outline-none disabled:opacity-60"
              />
              <button
                type="button"
                onClick={send}
                disabled={!canSend}
                aria-label="Vraag stellen"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-800 text-white transition-colors hover:bg-brand-700 disabled:opacity-40"
              >
                <SendHorizontalIcon size={17} />
              </button>
            </div>
          </div>
        </div>
        <p className="px-1 pt-1.5 text-[11px] text-ink-900/45">
          {PII_NOTE} Mila geeft geen diagnose of medicatieadvies.
        </p>
      </div>

      {sheet && (
        <div className="fixed inset-0 z-50" onClick={() => setSheet(false)}>
          <div className="absolute inset-0 bg-ink-900/30" />
          <div
            role="dialog"
            aria-label="Meer opties"
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-3 shadow-xl"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ink-900/15" />
            <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-ink-900/40">
              Richting van je vraag
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {INTENTS.map(({ mode, label, hint, Icon }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setArmed(mode);
                    setSheet(false);
                    p.inputRef.current?.focus();
                  }}
                  className={`rounded-xl border p-3 text-left ${
                    armed === mode
                      ? 'border-brand-700 bg-brand-50'
                      : 'border-ring2 hover:border-brand-700'
                  }`}
                >
                  <div className="flex items-center gap-2 text-[14px] font-semibold text-ink-900">
                    <Icon size={16} className="text-brand-800" /> {label}
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-ink-900/55">{hint}</div>
                </button>
              ))}
            </div>
            <div className="mt-3 divide-y divide-brand-50 rounded-xl border border-ring2">
              <SheetRow
                Icon={BookTextIcon}
                label={`Zoekbereik: ${p.scopeLabel}`}
                onClick={() => {
                  setSheet(false);
                  p.onOpenScope();
                }}
              >
                {p.scopeActive && (
                  <button
                    type="button"
                    onClick={p.onClearScope}
                    className="text-[12px] text-ink-900/50"
                  >
                    wissen
                  </button>
                )}
              </SheetRow>
              <SheetRow
                Icon={HistoryIcon}
                label="Vorige gesprekken"
                onClick={() => {
                  setSheet(false);
                  p.onOpenHistory();
                }}
              />
              <SheetRow
                Icon={SquarePenIcon}
                label="Nieuw gesprek"
                onClick={() => {
                  setSheet(false);
                  p.onNewChat();
                }}
              />
              <SheetRow Icon={HelpCircleIcon} label="Mila geeft geen diagnose of medicatieadvies" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SheetRow({
  Icon,
  label,
  onClick,
  children,
}: {
  Icon: LucideIcon;
  label: string;
  onClick?: () => void;
  children?: ReactNode;
}): JSX.Element {
  const inner = (
    <>
      <Icon size={16} className="shrink-0 text-ink-900/60" />
      <span className="flex-1">{label}</span>
    </>
  );
  // Knop en eventuele actie (bijv. "wissen") zijn siblings: een button in
  // een button is ongeldige HTML en activeert soms allebei.
  return (
    <div className="flex items-center gap-3 px-3 text-[14px] text-ink-900">
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex flex-1 items-center gap-3 py-3 text-left hover:text-brand-800"
        >
          {inner}
        </button>
      ) : (
        <div className="flex flex-1 items-center gap-3 py-3 text-ink-900/60">{inner}</div>
      )}
      {children}
    </div>
  );
}

/** Onder Tailwind's md-grens (768px) rendert de pagina de mobiele schil. */
export function useIsMobile(): boolean {
  const query = '(max-width: 767px)';
  const [mobile, setMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (): void => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}
